import axios from 'axios';
import getFirebaseAuthToken from './fireBaseAuthenticationService';
import { BACKEND_URL, LOCATION_NAME_TO_ID } from '../utils/constants';

const DAYSMART_TIMEOUT = 60000; // 60 seconds for DaySmart API calls

/**
 * Location constants for DaySmart
 */
export const DAYSMART_LOCATIONS = {
    HOPKINS: 1,
    INVER_GROVE: 2,
    ALL: null  // null means fetch from all locations
};

/**
 * Fetch appointments from DaySmart for a date range
 * 
 * @param {string} startDate - ISO date string (e.g., "2026-01-12T00:00:00")
 * @param {string} endDate - ISO date string (e.g., "2026-01-12T23:59:59")
 * @param {number|null} locationId - Location ID (1=Hopkins, 2=Inver Grove, null=all)
 * @param {number[]} employeeIds - Optional array of DaySmart employee IDs to filter by
 * @param {boolean} includeClosed - If true, include closed tickets (for scheduling view)
 * @returns {Promise<{appointments: Array, count: number}>}
 */
export const fetchAppointments = async (startDate, endDate, locationId = null, employeeIds = null, includeClosed = false) => {
    try {
        const token = await getFirebaseAuthToken();
        
        const payload = {
            start_date: startDate,
            end_date: endDate,
            include_closed: includeClosed,
        };
        
        if (locationId !== null) {
            payload.location_id = locationId;
        }
        
        if (employeeIds && employeeIds.length > 0) {
            payload.employee_ids = employeeIds;
        }
        
        const response = await axios.post(`${BACKEND_URL}/daysmart/appointments`, 
            payload,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                timeout: DAYSMART_TIMEOUT
            }
        );

        return response.data;
    } catch (error) {
        console.error("Error fetching DaySmart appointments:", JSON.stringify(error.toJSON?.() || error));
        const errorMessage = error?.response?.data?.error || error?.message || "Unknown error";
        throw new Error(`Failed to fetch appointments: ${errorMessage}`);
    }
};

/**
 * Fetch today's appointments from DaySmart
 * 
 * @param {number|null} locationId - Location ID (1=Hopkins, 2=Inver Grove, null=all)
 * @returns {Promise<{appointments: Array, count: number}>}
 */
export const fetchTodaysAppointments = async (locationId = null) => {
    const today = new Date();
    const startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0);
    const endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);
    
    return fetchAppointments(
        startDate.toISOString(),
        endDate.toISOString(),
        locationId
    );
};

/**
 * Fetch appointments for a specific date
 * 
 * @param {Date} date - The date to fetch appointments for
 * @param {number|null} locationId - Location ID (1=Hopkins, 2=Inver Grove, null=all)
 * @returns {Promise<{appointments: Array, count: number}>}
 */
export const fetchAppointmentsForDate = async (date, locationId = null) => {
    const startDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0);
    const endDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59);
    
    return fetchAppointments(
        startDate.toISOString(),
        endDate.toISOString(),
        locationId
    );
};

/**
 * Fetch an employee's appointments for a specific date across their mapped locations.
 *
 * @param {Date} date - The date to fetch appointments for
 * @param {Object} locationToDaysmartIdMap - Map of location_id -> DaySmart employee ID
 * @param {boolean} includeClosed - If true, include closed tickets (full day book)
 * @returns {Promise<{appointments: Array, count: number}>}
 */
export const fetchEmployeeAppointmentsForDate = async (date, locationToDaysmartIdMap, includeClosed = true) => {
    const startDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0);
    const endDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59);
    const startISO = startDate.toISOString();
    const endISO = endDate.toISOString();

    const entries = Object.entries(locationToDaysmartIdMap || {})
        .map(([locId, dsId]) => [parseInt(locId, 10), parseInt(dsId, 10)])
        .filter(([locId, dsId]) => Number.isFinite(locId) && locId > 0 && Number.isFinite(dsId) && dsId > 0);

    if (entries.length === 0) {
        return { appointments: [], count: 0 };
    }

    const results = await Promise.all(
        entries.map(([locId, dsId]) =>
            fetchAppointments(startISO, endISO, locId, [dsId], includeClosed)
                .catch((error) => {
                    console.error(`Error fetching appointments for location ${locId}:`, error);
                    return { appointments: [] };
                })
        )
    );

    const appointments = results
        .flatMap((r) => r.appointments || [])
        .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));

    return { appointments, count: appointments.length };
};

/**
 * Fetch employee list from DaySmart for a location
 * 
 * @param {number} locationId - Location ID (1=Hopkins, 2=Inver Grove)
 * @returns {Promise<{employees: Array, count: number}>}
 */
export const fetchDaysmartEmployees = async (locationId) => {
    if (!locationId) {
        throw new Error("locationId is required");
    }
    
    try {
        const token = await getFirebaseAuthToken();
        
        const response = await axios.get(`${BACKEND_URL}/daysmart/employees`, {
            params: { location_id: locationId },
            headers: {
                Authorization: `Bearer ${token}`,
            },
            timeout: DAYSMART_TIMEOUT
        });

        return response.data;
    } catch (error) {
        console.error("Error fetching DaySmart employees:", JSON.stringify(error.toJSON?.() || error));
        const errorMessage = error?.response?.data?.error || error?.message || "Unknown error";
        throw new Error(`Failed to fetch employees: ${errorMessage}`);
    }
};

/**
 * Fetch employees from all DaySmart locations
 * 
 * @returns {Promise<{hopkins: Array, inverGrove: Array}>}
 */
export const fetchAllDaysmartEmployees = async () => {
    const [hopkinsResult, inverGroveResult] = await Promise.all([
        fetchDaysmartEmployees(DAYSMART_LOCATIONS.HOPKINS),
        fetchDaysmartEmployees(DAYSMART_LOCATIONS.INVER_GROVE)
    ]);
    
    return {
        hopkins: hopkinsResult.employees,
        inverGrove: inverGroveResult.employees,
        all: [...hopkinsResult.employees, ...inverGroveResult.employees]
    };
};

/**
 * Fetch notes for a client from DaySmart
 * 
 * @param {number} locationId - Location ID (1=Hopkins, 2=Inver Grove)
 * @param {number} clientId - DaySmart client ID
 * @returns {Promise<{notes: Array, count: number}>}
 */
export const fetchClientNotes = async (locationId, clientId) => {
    if (!locationId || !clientId) {
        throw new Error("locationId and clientId are required");
    }
    
    try {
        const token = await getFirebaseAuthToken();
        
        const response = await axios.post(`${BACKEND_URL}/daysmart/client/notes`, 
            {
                location_id: locationId,
                client_id: clientId
            },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                timeout: DAYSMART_TIMEOUT
            }
        );

        return response.data;
    } catch (error) {
        console.error("Error fetching DaySmart client notes:", JSON.stringify(error.toJSON?.() || error));
        const errorMessage = error?.response?.data?.error || error?.message || "Unknown error";
        throw new Error(`Failed to fetch client notes: ${errorMessage}`);
    }
};

/**
 * Helper to convert location name to ID
 * 
 * @param {string} locationName - "Hopkins" or "InverGrove"
 * @returns {number} Location ID
 */
export const getLocationIdByName = (locationName) => {
    return LOCATION_NAME_TO_ID[locationName] || null;
};

/**
 * Group appointments by employee
 * 
 * @param {Array} appointments - Array of appointment objects
 * @returns {Object} Appointments grouped by employee_id
 */
export const groupAppointmentsByEmployee = (appointments) => {
    return appointments.reduce((acc, appt) => {
        const empId = appt.employee_id || 'unassigned';
        if (!acc[empId]) {
            acc[empId] = {
                employee_name: appt.employee_name || 'Unassigned',
                appointments: []
            };
        }
        acc[empId].appointments.push(appt);
        return acc;
    }, {});
};

/**
 * Group appointments by client
 * 
 * @param {Array} appointments - Array of appointment objects
 * @returns {Object} Appointments grouped by client_id
 */
export const groupAppointmentsByClient = (appointments) => {
    return appointments.reduce((acc, appt) => {
        const clientId = appt.client_id || 'unknown';
        if (!acc[clientId]) {
            acc[clientId] = {
                client_name: appt.client_name || 'Unknown',
                appointments: []
            };
        }
        acc[clientId].appointments.push(appt);
        return acc;
    }, {});
};

/**
 * Filter appointments by status
 * 
 * @param {Array} appointments - Array of appointment objects
 * @param {string|string[]} statuses - Status or array of statuses to filter by
 * @returns {Array} Filtered appointments
 */
export const filterAppointmentsByStatus = (appointments, statuses) => {
    const statusArray = Array.isArray(statuses) ? statuses : [statuses];
    return appointments.filter(appt => 
        statusArray.some(s => appt.status?.toLowerCase() === s.toLowerCase())
    );
};

/**
 * Search type options for client search
 */
export const CLIENT_SEARCH_TYPES = {
    ALL: 'all',
    CLIENT: 'client',
    PET: 'pet',
    SPECIES: 'species',
    BREED: 'breed'
};

/**
 * Search for clients in DaySmart
 * 
 * @param {string} searchValue - Search term
 * @param {string} searchType - Type of search: "all", "client", "pet", "species", or "breed"
 * @param {number|null} locationId - Location ID (1=Hopkins, 2=Inver Grove, null=all)
 * @param {number} maxResults - Maximum results per location (default: 100)
 * @param {boolean} includeInactive - Include inactive clients (default: false)
 * @returns {Promise<{clients: Array, count: number, search_value: string, search_type: string}>}
 */
export const searchClients = async (searchValue, searchType = 'all', locationId = null, maxResults = 100, includeInactive = false) => {
    if (!searchValue || !searchValue.trim()) {
        throw new Error("Search value is required");
    }
    
    try {
        const token = await getFirebaseAuthToken();
        
        const payload = {
            search_value: searchValue.trim(),
            search_type: searchType,
            max_results: maxResults,
            include_inactive: includeInactive
        };
        
        if (locationId !== null) {
            payload.location_id = locationId;
        }
        
        const response = await axios.post(`${BACKEND_URL}/daysmart/clients/search`, 
            payload,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                timeout: DAYSMART_TIMEOUT
            }
        );

        return response.data;
    } catch (error) {
        console.error("Error searching DaySmart clients:", JSON.stringify(error.toJSON?.() || error));
        const errorMessage = error?.response?.data?.error || error?.message || "Unknown error";
        throw new Error(`Failed to search clients: ${errorMessage}`);
    }
};

export const fetchTicketDetails = async (ticketId, locationId) => {
    try {
        const token = await getFirebaseAuthToken();
        
        const response = await axios.get(`${BACKEND_URL}/daysmart/tickets/${ticketId}`, {
            params: { location_id: locationId },
            headers: {
                Authorization: `Bearer ${token}`,
            },
            timeout: DAYSMART_TIMEOUT
        });

        return response.data;
    } catch (error) {
        console.error("Error fetching DaySmart ticket details:", JSON.stringify(error.toJSON?.() || error));
        const errorMessage = error?.response?.data?.error || error?.message || "Unknown error";
        throw new Error(`Failed to fetch ticket details: ${errorMessage}`);
    }
};

/**
 * Close a ticket in DaySmart with payment and optional tips split across employees
 * 
 * @param {number} ticketId - DaySmart ticket ID
 * @param {number} locationId - Location ID (1=Hopkins, 2=Inver Grove)
 * @param {number} paymentAmount - Payment amount in dollars (e.g., 50.00)
 * @param {Array<{employee_id: number, amount: number}>} tips - Array of tip allocations per employee
 * @returns {Promise<Object>} Closed ticket response
 */
export const closeTicket = async (ticketId, locationId, paymentAmount, tips = []) => {
    if (!ticketId || !locationId) {
        throw new Error("ticketId and locationId are required");
    }
    if (paymentAmount <= 0) {
        throw new Error("paymentAmount must be greater than 0");
    }
    
    try {
        const token = await getFirebaseAuthToken();
        
        const payload = {
            ticket_id: ticketId,
            location_id: locationId,
            payment_amount: paymentAmount,
            payment_type: 'Visa', // All Square transactions marked as Visa
            tips: tips, // Array of {employee_id, amount}
        };
        
        const response = await axios.post(`${BACKEND_URL}/daysmart/tickets/${ticketId}/close`, 
            payload,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                timeout: DAYSMART_TIMEOUT
            }
        );

        return response.data;
    } catch (error) {
        console.error("Error closing DaySmart ticket:", JSON.stringify(error.toJSON?.() || error));
        const errorMessage = error?.response?.data?.error || error?.message || "Unknown error";
        throw new Error(`Failed to close ticket: ${errorMessage}`);
    }
};

/**
 * Cancel an appointment in DaySmart
 * 
 * @param {number} ticketId - DaySmart ticket ID
 * @param {number} locationId - Location ID (1=Hopkins, 2=Inver Grove)
 * @param {boolean} noShow - Whether to mark as no-show (default false)
 * @param {string} cancelComment - Optional cancellation reason
 * @returns {Promise<Object>} Cancelled ticket response
 */
export const cancelAppointment = async (ticketId, locationId, noShow = false, cancelComment = '') => {
    if (!ticketId || !locationId) {
        throw new Error("ticketId and locationId are required");
    }

    try {
        const token = await getFirebaseAuthToken();

        const response = await axios.post(
            `${BACKEND_URL}/daysmart/tickets/${ticketId}/cancel`,
            {
                location_id: locationId,
                no_show: noShow,
                cancel_comment: cancelComment,
            },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                timeout: DAYSMART_TIMEOUT,
            }
        );

        return response.data;
    } catch (error) {
        console.error("Error cancelling DaySmart appointment:", JSON.stringify(error.toJSON?.() || error));
        const errorMessage = error?.response?.data?.error || error?.message || "Unknown error";
        throw new Error(`Failed to cancel appointment: ${errorMessage}`);
    }
};

export const getUpcomingAppointments = async (clientId, locationId) => {
    const token = await getFirebaseAuthToken();
    
    try {
        const response = await axios.get(
            `${BACKEND_URL}/daysmart/clients/${clientId}/upcoming-appointments`,
            {
                params: { location_id: locationId },
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                timeout: 45000,
            }
        );
        return response.data;
    } catch (error) {
        console.error("Error fetching upcoming appointments:", JSON.stringify(error.toJSON?.() || error));
        const errorMessage = error?.response?.data?.error || error?.message || "Unknown error";
        throw new Error(`Failed to fetch upcoming appointments: ${errorMessage}`);
    }
};

export const getPreviousAppointments = async (clientId, locationId) => {
    const token = await getFirebaseAuthToken();
    
    try {
        const response = await axios.get(
            `${BACKEND_URL}/daysmart/clients/${clientId}/previous-appointments`,
            {
                params: { location_id: locationId },
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                timeout: 45000,
            }
        );
        return response.data;
    } catch (error) {
        console.error("Error fetching previous appointments:", JSON.stringify(error.toJSON?.() || error));
        const errorMessage = error?.response?.data?.error || error?.message || "Unknown error";
        throw new Error(`Failed to fetch previous appointments: ${errorMessage}`);
    }
};

/**
 * Fetch phone number for a location
 * @param {number} locationId - Location ID (1=Hopkins, 2=Inver Grove)
 * @returns {Promise<string>} Phone number
 */
export const getLocationPhone = async (locationId) => {
    try {
        const token = await getFirebaseAuthToken();
        
        const response = await axios.get(`${BACKEND_URL}/locations/${locationId}/phone`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            timeout: 10000,
        });
        
        return response.data.phone;
    } catch (error) {
        console.error("Error fetching location phone:", JSON.stringify(error.toJSON?.() || error));
        const errorMessage = error?.response?.data?.error || error?.message || "Unknown error";
        throw new Error(`Failed to fetch location phone: ${errorMessage}`);
    }
};

export const getAppointmentPatterns = async (clientId, locationId) => {
    try {
        const token = await getFirebaseAuthToken();
        const response = await axios.get(
            `${BACKEND_URL}/daysmart/clients/${clientId}/appointment-patterns`,
            {
                params: { location_id: locationId },
                headers: { Authorization: `Bearer ${token}` },
                timeout: 30000,
            }
        );
        return response.data;
    } catch (error) {
        console.error("Error fetching appointment patterns:", error);
        const errorMessage = error?.response?.data?.error || error?.message || "Unknown error";
        throw new Error(`Failed to fetch appointment patterns: ${errorMessage}`);
    }
};

/**
 * Fetch reminder statuses for a specific date
 * 
 * @param {string} date - Date in YYYY-MM-DD format
 * @returns {Promise<{statuses: Object}>} - Object with keys like "locationId-appointmentId" and status info
 */
export const fetchReminderStatuses = async (date) => {
    try {
        const token = await getFirebaseAuthToken();
        const response = await axios.get(
            `${BACKEND_URL}/reminder-statuses`,
            {
                params: { date },
                headers: { Authorization: `Bearer ${token}` },
                timeout: 30000,
            }
        );
        return response.data;
    } catch (error) {
        console.error("Error fetching reminder statuses:", error);
        const errorMessage = error?.response?.data?.error || error?.message || "Unknown error";
        throw new Error(`Failed to fetch reminder statuses: ${errorMessage}`);
    }
};

/**
 * Save or update a reminder status for an appointment
 * 
 * @param {number} appointmentId - DaySmart appointment ID
 * @param {number} locationId - Location ID (1=Hopkins, 2=Inver Grove)
 * @param {string} appointmentDate - Date in YYYY-MM-DD format
 * @param {string} status - One of: not_reminded, text_sent, voicemail, spoke_to, text_confirmed
 * @returns {Promise<{success: boolean}>}
 */
/**
 * Fetch available appointment slots for a groomer on a given date
 * 
 * @param {number} locationId - Location ID (1=Hopkins, 2=Inver Grove)
 * @param {string} suggestedDate - Date to check (YYYY-MM-DD)
 * @param {number|null} employeeId - Optional employee ID to check
 * @param {string|null} employeeName - Optional employee name
 * @param {number} durationMinutes - Appointment duration in minutes (default 60)
 * @returns {Promise<{available_slots: Array}>}
 */
export const getAvailableSlots = async (locationId, suggestedDate, employeeId = null, employeeName = null, durationMinutes = 60, exactDate = false) => {
    try {
        const token = await getFirebaseAuthToken();
        const response = await axios.post(
            `${BACKEND_URL}/daysmart/availability`,
            {
                location_id: locationId,
                suggested_date: suggestedDate,
                employee_id: employeeId,
                employee_name: employeeName,
                duration_minutes: durationMinutes,
                exact_date: exactDate,
            },
            {
                headers: { Authorization: `Bearer ${token}` },
                timeout: 30000,
            }
        );
        return response.data;
    } catch (error) {
        console.error("Error fetching available slots:", error);
        const errorMessage = error?.response?.data?.error || error?.message || "Unknown error";
        throw new Error(`Failed to fetch available slots: ${errorMessage}`);
    }
};

export const fetchServices = async (locationId) => {
    try {
        const token = await getFirebaseAuthToken();
        const response = await axios.get(
            `${BACKEND_URL}/daysmart/services`,
            {
                params: { location_id: locationId },
                headers: { Authorization: `Bearer ${token}` },
                timeout: DAYSMART_TIMEOUT,
            }
        );
        return response.data;
    } catch (error) {
        console.error("Error fetching services:", error);
        const errorMessage = error?.response?.data?.error || error?.message || "Unknown error";
        throw new Error(`Failed to fetch services: ${errorMessage}`);
    }
};

export const bookAppointment = async (bookingData) => {
    try {
        const token = await getFirebaseAuthToken();
        const response = await axios.post(
            `${BACKEND_URL}/daysmart/book`,
            bookingData,
            {
                headers: { Authorization: `Bearer ${token}` },
                timeout: 30000,
            }
        );
        return response.data;
    } catch (error) {
        console.error("Error booking appointment:", error);
        const errorMessage = error?.response?.data?.error || error?.message || "Unknown error";
        throw new Error(`Failed to book appointment: ${errorMessage}`);
    }
};

export const saveReminderStatus = async (appointmentId, locationId, appointmentDate, status) => {
    try {
        const token = await getFirebaseAuthToken();
        const response = await axios.post(
            `${BACKEND_URL}/reminder-statuses`,
            {
                appointment_id: appointmentId,
                location_id: locationId,
                appointment_date: appointmentDate,
                status: status,
            },
            {
                headers: { Authorization: `Bearer ${token}` },
                timeout: 30000,
            }
        );
        return response.data;
    } catch (error) {
        console.error("Error saving reminder status:", error);
        const errorMessage = error?.response?.data?.error || error?.message || "Unknown error";
        throw new Error(`Failed to save reminder status: ${errorMessage}`);
    }
};
